import type { MatchDisposition, MatchedIndividual } from "@repo/shared/types";
import { matchKey } from "@repo/shared/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./match-adjudication.i18n";

type MatchAdjudicationProps = {
  individuals: MatchedIndividual[];
  dispositions: Record<string, MatchDisposition>;
  onChange: (next: Record<string, MatchDisposition>) => void;
  disabled?: boolean;
};

/**
 * QL-b — decide, per matched individual, whether the listing IS this client.
 *
 * PEP/sanction lists carry no national id for a person: they record people by
 * name + birth date + country (`MatchedIndividual` has no id field, unlike
 * `MatchedEntity.corporateId`). So one search can return several people and
 * only a human can say which — if any — is the client. Without this the only
 * way to close such a review was `accepted`, which asserts the OPPOSITE fact:
 * that they ARE listed and the relationship is taken anyway.
 *
 * Matched ENTITIES are deliberately absent: a company match carries an org
 * number, so it is not ambiguous and there is nothing to rule out.
 *
 * Keys come from the shared `matchKey`, the same function `screeningBasisHash`
 * fingerprints matches with — so a disposition can never be attributed to a
 * different match than the one it was made about, and re-screening that
 * returns the same people keeps these verdicts.
 */
export function MatchAdjudication({
  individuals,
  dispositions,
  onChange,
  disabled = false,
}: MatchAdjudicationProps) {
  const t = useT(T);
  if (individuals.length === 0) {
    return null;
  }

  const set = (key: string, value: MatchDisposition) => {
    const next = { ...dispositions };
    // Clicking the active choice again clears it, back to undecided — a
    // reviewer who mis-clicks must be able to get back to "I have not said",
    // which is not the same as either verdict.
    if (next[key] === value) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Label>{t.matches}</Label>
      <p className="text-xs text-muted-foreground">{t.matchesHint}</p>
      <ul className="space-y-2">
        {individuals.map((individual) => {
          const key = matchKey(individual);
          const current = dispositions[key];
          return (
            <li
              key={key}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{individual.name ?? "—"}</p>
                <p className="text-xs text-muted-foreground">
                  {[
                    individual.birthDate ? `${t.born} ${individual.birthDate}` : null,
                    individual.countries?.length ? individual.countries.join(", ") : null,
                    individual.listType,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {current === undefined && <Badge variant="warning">{t.undecided}</Badge>}
                <Button
                  type="button"
                  size="sm"
                  variant={current === "confirmed" ? "destructive" : "outline"}
                  onClick={() => set(key, "confirmed")}
                  disabled={disabled}
                >
                  {current === "confirmed" ? t.confirmed : t.confirm}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={current === "dismissed" ? "default" : "outline"}
                  onClick={() => set(key, "dismissed")}
                  disabled={disabled}
                >
                  {current === "dismissed" ? t.dismissed : t.dismiss}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
